import React, { useState } from "react";
import { Box, Button, Text, useDisclosure } from "@chakra-ui/react";
import colors from "../utils/colors";
import ViewHistoryCard from "../features/patients/components/MedicalHistoryCard";
import ReviewOfSystemsCard from "../features/patients/components/ReviewOfSytemsCard";
import MediaCard, {
  MediaFile,
} from "../features/patients/components/MediaCard";
import TestCard from "../features/patients/components/TestsCard";
import TreatmentCard from "../features/patients/components/TreatmentCard";
import DefinitiveDiagnosis from "../features/patients/components/DefinitiveDiagnosis";
import DifferentialDiagnosis from "../features/patients/components/DifferentialDiagnosis";
import ReferralForm from "./ReferralPage";

interface MedicalHistoryPageProps {
  selectedHistory: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  appointmentId: string;
  patientGender?: "male" | "female";
  patientId: string;
}

const MedicalHistoryPage: React.FC<MedicalHistoryPageProps> = ({
  selectedHistory,
  onChange,
  appointmentId,
  patientGender,
  patientId,
}) => {
  const [pastMedicalHistory, setPastMedicalHistory] = useState<string[]>([]);
  const [familyHistory, setFamilyHistory] = useState<string[]>([]);
  const [socialHistory, setSocialHistory] = useState<string[]>([]);
  const [medicationHistory, setMedicationHistory] = useState<string[]>([]);
  const [physicalFindings, setPhysicalFindings] = useState<string[]>([]);

  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [tests, setTest] = useState<string[]>([]);

  const addNewHistory = (type: string) => {
    if (type === "Past Medical History")
      setPastMedicalHistory([...pastMedicalHistory, ""]);
    if (type === "Family History") setFamilyHistory([...familyHistory, ""]);
    if (type === "Social History") setSocialHistory([...socialHistory, ""]);
    if (type === "Medication History")
      setMedicationHistory([...medicationHistory, ""]);
    if (type === "Physical Findings")
      setPhysicalFindings([...physicalFindings, ""]);
    if (type === "Tests") setTest([...tests, ""]);
  };

  return (
    <Box width="100%" mb={"16px"}>
      {selectedHistory === "Past Medical History" && (
        <>
          {pastMedicalHistory.map((_, index) => (
            <Box key={index} mt="20px">
              <ViewHistoryCard
                type="Past Medical History"
                appointmentId={appointmentId}
                patientId={patientId}
                patientGender={patientGender}
              />
            </Box>
          ))}
          <Text
            color={colors.primary}
            fontWeight={700}
            fontSize={"16px"}
            mt="20px"
            cursor="pointer"
            onClick={() => addNewHistory("Past Medical History")}
          >
            + Add new past medical history
          </Text>
        </>
      )}
      {selectedHistory === "Family History" && (
        <>
          {familyHistory.map((_, index) => (
            <Box key={index} mt="20px">
              <ViewHistoryCard
                type="Family History"
                appointmentId={appointmentId}
                patientId={patientId}
                patientGender={patientGender}
              />
            </Box>
          ))}
          <Text
            color={colors.primary}
            fontWeight={700}
            fontSize={"16px"}
            mt="20px"
            cursor="pointer"
            onClick={() => addNewHistory("Family History")}
          >
            + Add new condition
          </Text>
        </>
      )}
      {selectedHistory === "Social History" && (
        <>
          {socialHistory.map((_, index) => (
            <Box key={index} mt="20px">
              <ViewHistoryCard
                type="Social History"
                appointmentId={appointmentId}
                patientId={patientId}
                patientGender={patientGender}
              />
            </Box>
          ))}
          <Text
            color={colors.primary}
            fontWeight={700}
            fontSize={"16px"}
            mt="20px"
            cursor="pointer"
            onClick={() => addNewHistory("Social History")}
          >
            + Add new history
          </Text>
        </>
      )}
      {selectedHistory === "Medication History" && (
        <>
          {medicationHistory.map((_, index) => (
            <Box key={index} mt="20px">
              <ViewHistoryCard
                type="Medication History"
                appointmentId={appointmentId}
                patientId={patientId}
                patientGender={patientGender}
              />
            </Box>
          ))}
          <Text
            color={colors.primary}
            fontWeight={700}
            fontSize={"16px"}
            mt="20px"
            cursor="pointer"
            onClick={() => addNewHistory("Medication History")}
          >
            + Add new medication
          </Text>
        </>
      )}
      {selectedHistory === "Physical Findings" && (
        <>
          {physicalFindings.map((_, index) => (
            <Box key={index} mt="20px">
              <ViewHistoryCard
                type="Physical Findings"
                appointmentId={appointmentId}
                patientId={patientId}
                patientGender={patientGender}
              />
            </Box>
          ))}
          <Text
            color={colors.primary}
            fontWeight={700}
            fontSize={"16px"}
            mt="20px"
            cursor="pointer"
            onClick={() => addNewHistory("Physical Findings")}
          >
            + Add new physical finding
          </Text>
        </>
      )}
      {selectedHistory === "Treatment" && (
        <Box mt="20px">
          <TreatmentCard
            type="Treatment"
            appointmentId={appointmentId}
            patientId={patientId}
            patientGender={patientGender}
          />
        </Box>
      )}
      {selectedHistory === "Review of Systems" && (
        <Box mt="20px">
          <ReviewOfSystemsCard appointmentId={appointmentId} />
        </Box>
      )}
      {selectedHistory === "Tests" && (
        <>
          {tests.map((_, index) => (
            <Box key={index} mt="20px">
              <TestCard
                type="Tests"
                appointmentId={appointmentId}
                patientId={patientId}
              />
            </Box>
          ))}
          <Text
            color={colors.primary}
            fontWeight={700}
            fontSize={"16px"}
            mt="20px"
            cursor="pointer"
            onClick={() => addNewHistory("Tests")}
          >
            + Add new Test
          </Text>
        </>
      )}
      {selectedHistory === "Differential Diagnosis" && (
        <Box>
          <DifferentialDiagnosis appointmentId={appointmentId} />
        </Box>
      )}
      {selectedHistory === "Definitive Diagnosis" && (
        <Box>
          <DefinitiveDiagnosis appointmentId={appointmentId} />
        </Box>
      )}
      {selectedHistory === "Media" && (
        <Box mt="20px">
          <MediaCard appointmentId={appointmentId} />
        </Box>
      )}

      {selectedHistory === "Refer Patient" && (
        <Box mt="20px">
          <ReferralForm appointmentId={appointmentId} patientId={patientId} />
        </Box>
      )}
    </Box>
  );
};

export default MedicalHistoryPage;
